/**
 * Shared contract for the frontend log pipeline: the browser transport
 * (`web/app-home/src/lib/logger.ts`) and the collector
 * (`src/api/frontend-logs.ts`, also mounted at the dev alias) agree on these
 * bounds. Pure and non-throwing: it runs on attacker-controlled input.
 */
import { z } from 'zod';

/** Longest string field (msg, error, category, component, pathname, release) the collector keeps. */
export const FRONTEND_LOG_MAX_FIELD_LEN = 500;

/** Most distinct issue shapes a client sends / the collector logs per entry (plus one "truncated" marker). */
export const FRONTEND_LOG_MAX_ISSUES = 10;

/** Upper bound on the serialized `issues` the collector writes to the log. */
export const FRONTEND_LOG_MAX_ISSUES_CHARS = 2000;

/** Bounded diagnostic context the transport sends and the collector logs, besides msg/issues. */
export const FRONTEND_LOG_CONTEXT_FIELDS = ['endpoint', 'section', 'kind', 'eventName', 'stateKey'] as const;

/** A schema-vocabulary word (an issue code, type or format name): never free text. */
export const LOG_TOKEN_PATTERN = /^[a-z0-9_]{1,40}$/;
const Token = z.string().regex(LOG_TOKEN_PATTERN);

/**
 * One logged validation-failure shape. Boundary, stated honestly: the collector accepts any token-shaped
 * `code` and any <=400-char `path` from an authenticated browser. Privacy of path text is enforced
 * BROWSER-side in `summarizeIssues` (it alone knows each issue's `origin`); the server only guarantees
 * shape and size, as it does for the 500-char `msg` an authenticated user can already send verbatim. Built in the browser by `summarizeIssues`
 * (the only place that masks record keys: it has the issue's `origin`), and
 * re-validated by the collector with this same schema; anything that does not
 * fit is dropped, so free text can never ride in.
 */
export const LoggedIssueSchema = z
  .object({
    code: Token,
    /** Dotted schema path, array indices collapsed to `[]`; `(root)` when empty. */
    path: z.string().max(400),
    count: z.number().int().min(0).max(1e9),
    expected: Token.optional(),
    format: Token.optional(),
  })
  .strict();
export type IssueShape = z.infer<typeof LoggedIssueSchema>;

const WRITE_SUFFIX = / *\(write\)$/;
const MAX_ENDPOINT_LEN = 200;

/**
 * Loggable label for a request: path only. Everything from the first `?` or
 * `#` onward (whitespace included) is cut BEFORE anything else happens, so no
 * query or fragment text can reach a log field. Tolerates non-URL labels such
 * as `sse:artifact:updated`; the recognized ` (write)` annotation is stripped
 * first and re-appended after the cut. Used by the browser when it builds the
 * `[api-validation]` message AND by the collector on the messages it receives
 * (old tabs still send the raw URL).
 */
export function endpointLabel(input: string): string {
  let label = input.trim();
  const isWrite = WRITE_SUFFIX.test(label);
  if (isWrite) label = label.replace(WRITE_SUFFIX, '');
  const cut = label.search(/[?#]/);
  if (cut >= 0) label = label.slice(0, cut);
  label = label.trim();
  // Absolute, protocol-relative (`//host/p`) or tab-split (`https:\t//host/p`) URLs reduce to their path.
  // A fixed base keeps the parse offline; explicit non-URL labels (`sse:artifact:updated`) are left alone.
  if (/^(?:https?:[\t\n\r ]*)?\/\//i.test(label)) {
    try {
      label = new URL(label, 'http://x.invalid').pathname;
    } catch {
      label = label.replace(/^(?:https?:[\t\n\r ]*)?\/\/[^/]*/i, '');
    }
  }
  if (label === '') label = 'unknown';
  if (label.length > MAX_ENDPOINT_LEN) label = `${label.slice(0, MAX_ENDPOINT_LEN)}...`;
  return isWrite ? `${label} (write)` : label;
}

export const API_VALIDATION_PREFIX = '[api-validation] ';
